import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Login as LoginIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { format, formatDistanceToNow } from 'date-fns';

// Audit log action types
const ACTION_TYPES = {
  USER_LOGIN: 'user_login',
  USER_LOGOUT: 'user_logout',
  USER_CREATED: 'user_created',
  USER_UPDATED: 'user_updated',
  USER_DELETED: 'user_deleted',
  ROLE_CHANGED: 'role_changed',
  PERMISSION_CHANGED: 'permission_changed',
  FAMILY_CREATED: 'family_created',
  FAMILY_JOINED: 'family_joined',
  SETTINGS_CHANGED: 'settings_changed',
  TICKET_CREATED: 'ticket_created',
  TICKET_RESOLVED: 'ticket_resolved',
  ADMIN_ACTION: 'admin_action'
};

const ACTION_LABELS = {
  [ACTION_TYPES.USER_LOGIN]: 'User Login',
  [ACTION_TYPES.USER_LOGOUT]: 'User Logout',
  [ACTION_TYPES.USER_CREATED]: 'User Created',
  [ACTION_TYPES.USER_UPDATED]: 'User Updated',
  [ACTION_TYPES.USER_DELETED]: 'User Deleted',
  [ACTION_TYPES.ROLE_CHANGED]: 'Role Changed',
  [ACTION_TYPES.PERMISSION_CHANGED]: 'Permission Changed',
  [ACTION_TYPES.FAMILY_CREATED]: 'Family Created',
  [ACTION_TYPES.FAMILY_JOINED]: 'Family Joined',
  [ACTION_TYPES.SETTINGS_CHANGED]: 'Settings Changed',
  [ACTION_TYPES.TICKET_CREATED]: 'Ticket Created',
  [ACTION_TYPES.TICKET_RESOLVED]: 'Ticket Resolved',
  [ACTION_TYPES.ADMIN_ACTION]: 'Admin Action'
};

const getActionIcon = (actionType) => {
  switch (actionType) {
    case ACTION_TYPES.USER_LOGIN:
      return <LoginIcon fontSize="small" />;
    case ACTION_TYPES.USER_LOGOUT:
      return <LogoutIcon fontSize="small" />;
    case ACTION_TYPES.USER_CREATED:
    case ACTION_TYPES.FAMILY_JOINED:
      return <AddIcon fontSize="small" />;
    case ACTION_TYPES.USER_UPDATED:
    case ACTION_TYPES.SETTINGS_CHANGED:
      return <EditIcon fontSize="small" />;
    case ACTION_TYPES.USER_DELETED:
      return <DeleteIcon fontSize="small" />;
    case ACTION_TYPES.ROLE_CHANGED:
    case ACTION_TYPES.PERMISSION_CHANGED:
      return <SecurityIcon fontSize="small" />;
    default:
      return <SettingsIcon fontSize="small" />;
  }
};

const getActionColor = (actionType) => {
  switch (actionType) {
    case ACTION_TYPES.USER_LOGIN:
      return 'success';
    case ACTION_TYPES.USER_LOGOUT:
      return 'default';
    case ACTION_TYPES.USER_CREATED:
    case ACTION_TYPES.FAMILY_CREATED:
    case ACTION_TYPES.FAMILY_JOINED:
      return 'info';
    case ACTION_TYPES.USER_DELETED:
      return 'error';
    case ACTION_TYPES.ROLE_CHANGED:
    case ACTION_TYPES.PERMISSION_CHANGED:
      return 'warning';
    default:
      return 'default';
  }
};

const SystemAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');

  useEffect(() => {
    loadAuditLogs();
  }, []);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to load from auditLogs collection
      // If it doesn't exist yet, generate mock data from user activity
      let auditData = [];

      try {
        const auditSnapshot = await getDocs(
          query(collection(db, 'auditLogs'), orderBy('timestamp', 'desc'), limit(500))
        );
        auditData = auditSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (e) {
        logger.log('AuditLogs collection not found, generating from user data');
      }

      // If no audit logs exist, generate from user creation data
      if (auditData.length === 0) {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Generate audit entries from user data
        auditData = users
          .filter(u => u.createdAt)
          .map(user => ({
            id: `gen-${user.id}`,
            actionType: ACTION_TYPES.USER_CREATED,
            userId: user.id,
            userEmail: user.email,
            userName: user.displayName || `${user.firstName} ${user.lastName}`,
            details: `Account created for ${user.email}`,
            timestamp: user.createdAt,
            ipAddress: null,
            generated: true
          }))
          .sort((a, b) => {
            const aTime = a.timestamp?.toDate?.() || new Date(0);
            const bTime = b.timestamp?.toDate?.() || new Date(0);
            return bTime - aTime;
          });

        // Add family join events
        users
          .filter(u => u.familyCode && u.createdAt)
          .forEach(user => {
            auditData.push({
              id: `gen-family-${user.id}`,
              actionType: ACTION_TYPES.FAMILY_JOINED,
              userId: user.id,
              userEmail: user.email,
              userName: user.displayName || `${user.firstName} ${user.lastName}`,
              details: `Joined family: ${user.familyCode}`,
              timestamp: user.createdAt,
              ipAddress: null,
              generated: true
            });
          });

        // Sort again
        auditData.sort((a, b) => {
          const aTime = a.timestamp?.toDate?.() || new Date(0);
          const bTime = b.timestamp?.toDate?.() || new Date(0);
          return bTime - aTime;
        });
      }

      setLogs(auditData);
    } catch (err) {
      logger.error('Error loading audit logs:', err);
      setError('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  // Filter logs
  const filteredLogs = logs.filter(log => {
    const matchesSearch = searchTerm === '' ||
      log.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesAction = filterAction === 'all' || log.actionType === filterAction;

    return matchesSearch && matchesAction;
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Audit Logs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track system activity and user actions
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadAuditLogs}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.800' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by user, email, or details..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Action Type</InputLabel>
              <Select
                value={filterAction}
                label="Action Type"
                onChange={(e) => setFilterAction(e.target.value)}
              >
                <MenuItem value="all">All Actions</MenuItem>
                {Object.entries(ACTION_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary">
              {filteredLogs.length} entries
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Logs Table */}
      <TableContainer component={Paper} sx={{ bgcolor: 'grey.800' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Timestamp</TableCell>
              <TableCell>IP Address</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No audit logs found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredLogs
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Chip
                        icon={getActionIcon(log.actionType)}
                        label={ACTION_LABELS[log.actionType] || log.actionType}
                        size="small"
                        color={getActionColor(log.actionType)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{log.userName || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.userEmail || log.userId}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>
                        {log.details || '—'}
                      </Typography>
                      {log.generated && (
                        <Chip label="Generated" size="small" sx={{ ml: 1, height: 18, fontSize: 10 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={log.timestamp?.toDate ? format(log.timestamp.toDate(), 'PPpp') : '—'}>
                        <Typography variant="body2">
                          {log.timestamp?.toDate
                            ? formatDistanceToNow(log.timestamp.toDate(), { addSuffix: true })
                            : '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {log.ipAddress || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredLogs.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* Info Note */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Audit logging captures user activity automatically.
          Entries marked "Generated" are derived from existing user data.
          Real-time logging will capture all future actions.
        </Typography>
      </Alert>
    </Box>
  );
};

export default SystemAuditLogs;
